/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { AlertWithActionsType } from '@masfront/common-b2c'

import { makeStyles, Box, Typography } from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'

import useTariff from '../../hooks/useTariff'
import { resetTariffs } from '../../store/tariff.reducer'
import SelectedTariffCard from '../SelectedTariffCard/SelectedTariffCard'
import TariffCard from '../TariffCard/TariffCard'

import UseAnalytics from '~/analytics/hooks/useAnalytics'
import useModal from '~/modal/hooks/useModal'
import PATHS from '~/router/paths'
import { resetSaleFlow } from '~/sale/store/sale.reducer'

export const TARIFF_ERROR = 'Ha habido un error al intentar recuperar las tarifas'

const useStyles = makeStyles(() => ({
  cardsContainer: {
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    flexWrap: 'wrap',
    transition: 'maxHeight, opacity 0.3s ease-out',
  },
  top: {
    marginLeft: '20px',
  },
  skeletonCard: {
    margin: '0 28px 32px 28px',
  },
  card: {
    margin: '0 28px 32px 28px',
  },
  selectedTariffCardContainer: {
    transition: 'height, opacity 0.3s ease-out',
    width: '100%',
  },
  showSelectedTariffCard: {
    height: 'auto',
    margin: '28px 0 50px 0',
    opacity: '1',
  },
  hideSelectedTariffCard: {
    height: '0',
    opacity: '0',
  },
  showCardsContainer: {
    opacity: '1',
    maxHeight: 'auto',
  },
  hideShowCardsContainer: {
    maxHeight: '0',
    opacity: '0',
    zIndex: -1,
    pointerEvents: 'none',
  },
}))

export default function TariffPicker({ id }: { id: string }): React.ReactElement {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const { addToCart, removeFromCart } = UseAnalytics()
  const tariffCardsSqueleton = [1, 2, 3, 4]
  const {
    getTariffs,
    setTariff,
    tariffs,
    loading,
    selectedTariff,
    errorMessage,
    errorCode,
  } = useTariff(id)
  const { setModalConfig, setIsOpen } = useModal()

  const maxNumberOfPrices = useMemo(
    () => Math.max(0, ...tariffs.map((tariff) => (tariff.price ? tariff.price.length : 0))),
    [tariffs],
  )

  useEffect(() => {
    if (errorMessage || errorCode) {
      setModalConfig({
        open: false,
        type: AlertWithActionsType.error,
        title: TARIFF_ERROR,
        actions: [
          {
            dataHook: 'tariffs-retry',
            name: 'Probar de nuevo',
            fn: () => {
              getTariffs()
            },
          },
          {
            dataHook: 'cancel-sale',
            name: 'Cancelar',
            fn: () => {
              dispatch(resetSaleFlow())
              dispatch(resetTariffs())
              history.push(PATHS.SALES)
            },
          },
        ],
      })
      setIsOpen(true)
    }
  }, [errorMessage, errorCode])

  function selectCard(id: string): void {
    if (selectedTariff) return
    const tariffSelected = tariffs.filter((tariff) => tariff.id === id)[0]
    setTariff(tariffSelected)
    addToCart()
  }

  function resetTariff() {
    setTariff(null)
    removeFromCart()
  }

  return (
    <>
      <Box py="20px" px={6} display="flex" alignItems="center">
        <Typography variant="subtitle1">SELECCIONA LA TARIFA</Typography>
        {/* <Link href="/" underline="always" className={classes.top} onClick={() => showTop()}>
            Ver solo tarifas TOP
          </Link> */}
      </Box>
      <Box pl={12} pr={6}>
        <SelectedTariffCard
          id={id}
          className={`${classes.selectedTariffCardContainer}
          ${selectedTariff ? classes.showSelectedTariffCard : classes.hideSelectedTariffCard}`}
          title={selectedTariff?.name || ''}
          minutes={selectedTariff?.call[0]?.amount || 0}
          data={selectedTariff?.mobileData?.amount || 0}
          prices={selectedTariff?.price?.map((price) => price) || []}
          resetTariff={resetTariff}
        />
      </Box>
      <Box
        className={`${classes.cardsContainer}
        ${selectedTariff ? classes.hideShowCardsContainer : classes.showCardsContainer}`}
      >
        {loading
          ? tariffCardsSqueleton.map((el) => (
              <Skeleton
                key={el}
                className={classes.skeletonCard}
                animation="wave"
                variant="rect"
                width={192}
                height={300}
              />
            ))
          : tariffs.map((tariff) => (
              <TariffCard
                key={tariff.id}
                id={tariff.id}
                lineId={id}
                className={classes.card}
                selected={selectedTariff?.id === tariff.id}
                top={tariff.id === '109'} // temp
                title={tariff.name || ''}
                minutes={tariff.call[0]?.amount}
                data={tariff.mobileData?.amount || 0}
                prices={tariff.price?.map((price) => price) || []}
                selectCard={selectCard}
                maxNumberOfPrices={maxNumberOfPrices}
              />
            ))}
      </Box>
      {errorMessage && (
        <Box py={5} px={6}>
          <Typography variant="subtitle1" data-hook="tariffs-error">
            Error al obtener las tarifas
          </Typography>
        </Box>
      )}
    </>
  )
}
